import type {MinorAmount} from "core/format/minorUnits";
import type {Locale} from "i18n";
import {planIdForCode, type PaidPlanId} from "pricing/planCatalogue";
import {formatHostedUploadLimit} from "pricing/planEntitlements";
import {formatMinorMoney} from "pricing/PricingStorage";
import type {OverLimitPlanOffer, OverLimitPricing} from "./overLimitOffer";

/**
 * The over-limit pay dialog's rules (kitecorp/transferify-ui#3229, owner decisions of 2026-10-03), free of React.
 *
 * Neither wall sells a one-off any more (Instant: kitecorp/transferify-api-service#3005, Q5; hosted: plan W2, the row
 * gone since kitecorp/transferify-ui#3916), so the dialog's only row kind is a plan, the first month of one that lifts
 * the wall at a fixed price. The sized dialog (kitecorp/transferify-ui#3800) lists every plan that holds the drop and
 * chooses the smallest when it opens ({@link ./sizedPlanDialog.preselectedPlanRow}). Continue opens the payment step
 * for the chosen plan.
 */

/** One row of the dialog. */
export type PayRow = "plan";

/** Where the dialog is: choosing a row, or paying for the chosen one. */
export interface PayDialogState {
	step: "choose" | "pay";
	choice: PayRow | null;
}

/** The dialog starts here: nothing chosen yet (owner, 2026-10-03); the sized dialog then preselects its smallest plan. */
export const PAY_DIALOG_START: PayDialogState = {step: "choose", choice: null};

/** The sender picks a row. Only on the choosing step: the payment step's row was settled by Continue. */
export function choosePayRow(state: PayDialogState, row: PayRow): PayDialogState {
	return state.step === "choose" ? {step: "choose", choice: row} : state;
}

/** Whether Continue may be pressed: a row is chosen, and it is one the dialog still offers. */
export function mayContinue(state: PayDialogState, rows: readonly PayRow[]): boolean {
	return state.step === "choose" && state.choice !== null && rows.includes(state.choice);
}

/** Continue: the payment step for the chosen row, or no change while Continue may not be pressed. */
export function continuePayDialog(state: PayDialogState, rows: readonly PayRow[]): PayDialogState {
	return mayContinue(state, rows) ? {step: "pay", choice: state.choice} : state;
}

/**
 * Back from the payment step to the choice, keeping the chosen row. The consent is not kept: the payment step
 * starts unticked every time, as the compact dialog's does, so a second Continue meets a fresh form.
 */
export function backFromPayment(state: PayDialogState): PayDialogState {
	return {step: "choose", choice: state.choice};
}

/** The paid plan the api offered, or null for none (a free plan is never a row). */
export function offeredPlanId(planOffer: OverLimitPlanOffer | null | undefined): PaidPlanId | null {
	if (planOffer === null || planOffer === undefined || planOffer.planCode === "FREE") {
		return null;
	}
	const id = planIdForCode(planOffer.planCode);
	return id === "free" ? null : id;
}

/** The plan row's figures, formatted for the storefront the api quoted in. */
export interface PlanRowFigures {
	plan: PaidPlanId;
	/** The first invoice, VAT included. */
	firstMonth: string;
	/** The monthly price every later invoice charges, VAT included. */
	renewal: string;
	/** The plan's per-transfer hosted limit, or the unlimited label for a plan with none. */
	limit: string;
}

/** Every figure the plan row states, from the api's answer alone: nothing is derived from another figure here. */
export function planRowFigures(
	planOffer: OverLimitPlanOffer | null | undefined, locale: Locale, unlimitedLabel: string,
): PlanRowFigures | null {
	const plan = offeredPlanId(planOffer);
	if (plan === null || planOffer === null || planOffer === undefined) {
		return null;
	}
	return {
		plan,
		firstMonth: formatMinorMoney(planOffer.currency, planOffer.firstMonthMinor, locale),
		renewal: formatMinorMoney(planOffer.currency, planOffer.renewalMinor, locale),
		limit: formatHostedUploadLimit(planOffer.maxHostedUploadBytes ?? null, unlimitedLabel, locale),
	};
}

/**
 * The plan row's own funnel surface, one per wall for the reason the walls are two: the hosted wall and the Instant wall
 * convert differently, and averaging them would hide whichever one is failing.
 */
export function overLimitPlanMoment(pricing: OverLimitPricing): "OVER_LIMIT_HOSTED_PLAN" | "OVER_LIMIT_INSTANT_PLAN" {
	return pricing === "instant" ? "OVER_LIMIT_INSTANT_PLAN" : "OVER_LIMIT_HOSTED_PLAN";
}

/** The figure the plan row shows, as the funnel records it: the first month, which is what the row charges today. */
export function planRowPrice(planOffer: OverLimitPlanOffer | null | undefined): MinorAmount | null {
	return planOffer === null || planOffer === undefined
		? null : {currency: planOffer.currency, amountMinor: planOffer.firstMonthMinor};
}
